// SocialIcon — renders a branded square icon for a given social platform key.
// Accepts any string so both live SocialProvider values and coming-soon platform
// keys ("bluesky", "threads", etc.) can be rendered.  Unknown keys fall back to
// a neutral monogram using the first letter of the provider string.

interface Props {
  provider: string;
  size?: number;
}

interface IconDef {
  bg: string;
  gradient?: string;
  svg: React.ReactNode;
}

const icons: Record<string, IconDef> = {
  // ── Live providers ──────────────────────────────────────────────────────────
  meta_instagram: {
    bg: "",
    gradient:
      "linear-gradient(135deg, #833AB4 0%, #C13584 25%, #E1306C 50%, #FD1D1D 75%, #F77737 100%)",
    svg: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.5" fill="white" stroke="none" />
      </svg>
    ),
  },
  meta_facebook: {
    bg: "#1877F2",
    svg: (
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    ),
  },
  linkedin: {
    bg: "#0A66C2",
    svg: (
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    ),
  },
  x: {
    bg: "#000000",
    svg: (
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.741l7.73-8.835L1.254 2.25H8.08l4.261 5.636zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },

  // ── Coming-soon providers ───────────────────────────────────────────────────
  threads: {
    bg: "#000000",
    svg: (
      // Threads "@"-style spiral logomark
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M12 2C7.03 2 3 6.03 3 11c0 3.56 2 6.65 4.93 8.22-.07-.62-.13-1.57.03-2.25.14-.6.93-3.95.93-3.95s-.24-.48-.24-1.18c0-1.11.64-1.94 1.44-1.94.68 0 1.01.51 1.01 1.12 0 .68-.44 1.71-.66 2.66-.19.79.39 1.44 1.16 1.44 1.4 0 2.34-1.79 2.34-3.91 0-1.62-1.1-2.75-2.67-2.75-1.82 0-2.88 1.36-2.88 2.77 0 .55.21 1.14.47 1.46.05.06.06.12.04.18l-.17.71c-.03.11-.1.14-.22.08-.97-.45-1.58-1.87-1.58-3.01 0-2.44 1.77-4.69 5.11-4.69 2.68 0 4.76 1.91 4.76 4.46 0 2.66-1.68 4.8-4.01 4.8-.78 0-1.52-.41-1.77-.89l-.48 1.81c-.17.67-.64 1.5-.95 2.01.71.22 1.47.34 2.25.34 4.97 0 9-4.03 9-9S16.97 2 12 2z" />
      </svg>
    ),
  },
  bluesky: {
    bg: "#0085FF",
    svg: (
      // Bluesky butterfly-inspired mark
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.204-.659-.299-1.664-.621-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z" />
      </svg>
    ),
  },
  mastodon: {
    bg: "#6364FF",
    svg: (
      // Mastodon elephant-inspired "M" mark
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M21.327 8.566c0-4.339-2.843-5.61-2.843-5.61-1.433-.658-3.894-.935-6.451-.956h-.063c-2.557.021-5.016.298-6.45.956 0 0-2.843 1.272-2.843 5.61 0 .993-.019 2.181.012 3.441.103 4.243.778 8.425 4.701 9.463 1.809.479 3.362.579 4.612.51 2.268-.126 3.542-.809 3.542-.809l-.075-1.646s-1.621.511-3.441.449c-1.804-.062-3.707-.194-3.999-2.409a4.523 4.523 0 0 1-.04-.621s1.77.433 4.014.536c1.372.063 2.658-.08 3.965-.236 2.506-.299 4.688-1.843 4.962-3.254.434-2.223.4-5.424.4-5.424zm-3.353 5.59h-2.081V9.057c0-1.075-.452-1.62-1.357-1.62-1 0-1.501.647-1.501 1.927v2.791h-2.069V9.364c0-1.28-.501-1.927-1.502-1.927-.905 0-1.357.545-1.357 1.62v5.099H6.026V8.903c0-1.074.273-1.927.823-2.558.567-.631 1.313-.955 2.24-.955 1.07 0 1.881.411 2.416 1.234l.521.873.521-.873c.535-.823 1.346-1.234 2.416-1.234.927 0 1.673.324 2.239.955.551.631.824 1.484.824 2.558v5.253z" />
      </svg>
    ),
  },
  tiktok: {
    bg: "#010101",
    svg: (
      // TikTok musical-note-inspired mark
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.23 8.23 0 0 0 4.81 1.54V6.78a4.85 4.85 0 0 1-1.04-.09z" />
      </svg>
    ),
  },
  pinterest: {
    bg: "#E60023",
    svg: (
      // Pinterest "P" pin mark
      <svg viewBox="0 0 24 24" fill="white">
        <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
      </svg>
    ),
  },
  truth_social: {
    bg: "#5448EF",
    svg: (
      // Truth Social "T" wordmark
      <svg viewBox="0 0 24 24" fill="white">
        <text
          x="12"
          y="17"
          textAnchor="middle"
          fontSize="16"
          fontWeight="bold"
          fontFamily="sans-serif"
        >
          T
        </text>
      </svg>
    ),
  },
};

/** Fallback: grey square with the first letter of the provider key. */
function FallbackIcon({ provider, size }: { provider: string; size: number }) {
  const letter = provider.charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        background: "#9CA3AF",
        borderRadius: "0.625rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "white",
        fontWeight: 700,
        fontSize: size * 0.4,
        fontFamily: "sans-serif",
      }}
    >
      {letter}
    </div>
  );
}

const SocialIcon = ({ provider, size = 44 }: Props) => {
  const def = icons[provider];

  if (!def) return <FallbackIcon provider={provider} size={size} />;

  const { bg, gradient, svg } = def;

  return (
    <div
      style={{
        width: size,
        height: size,
        background: gradient ?? bg,
        borderRadius: "0.625rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <div style={{ width: size * 0.52, height: size * 0.52 }}>{svg}</div>
    </div>
  );
};

export { SocialIcon };
